/**
 * ChangesDiff Component
 *
 * Feature: 123-audit-log-viewer
 * Description: Display field-level diffs between old and new values
 * Constitution: v1.12.0
 */

import React, { useState } from 'react';
import type { FieldChange } from '../../../types/audit';

const TRUNCATE_LENGTH = 200;

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Pull every GUID out of any value (string / array / object) — used to gather the GUIDs in an
 *  audit payload so they can be resolved to friendly names. */
export function extractGuids(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(extractGuids);
  const s = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return s.match(GUID_RE) ?? [];
}

/** Friendly names for the GUIDs found in a value, de-duplicated (lowercase-keyed lookup). */
function namesFor(values: unknown[], nameMap: Record<string, string>): string[] {
  const names = values
    .flatMap((v) => extractGuids(v))
    .map((g) => nameMap[g.toLowerCase()])
    .filter((n): n is string => !!n);
  return [...new Set(names)];
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(none)';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function TruncatedValue({ value }: { value: unknown }) {
  const [expanded, setExpanded] = useState(false);
  const str = formatValue(value);

  if (str.length <= TRUNCATE_LENGTH) {
    return <span>{str}</span>;
  }

  if (expanded) {
    return (
      <span>
        {str}{' '}
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-[var(--color-accent)] hover:underline"
        >
          Show less
        </button>
      </span>
    );
  }

  return (
    <span>
      {str.substring(0, TRUNCATE_LENGTH)}…{' '}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-[var(--color-accent)] hover:underline"
      >
        Show full
      </button>
    </span>
  );
}

interface ChangesDiffProps {
  changes: FieldChange[];
  /** GUID (lowercase) → friendly name. When a change value contains a resolved GUID, the human
   *  name is shown alongside the raw value (the GUID is kept for audit traceability). */
  nameMap?: Record<string, string>;
}

export default function ChangesDiff({ changes, nameMap = {} }: ChangesDiffProps) {
  if (!changes || changes.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-secondary)]">No changes detected</p>
    );
  }

  return (
    <dl className="m-0">
      {changes.map((change) => {
        const names = namesFor([change.oldValue, change.newValue], nameMap);
        return (
          <div key={change.fieldName} className="mb-1.5">
            <dt className="font-mono text-[13px] font-semibold text-[var(--color-text-primary)]">
              {change.fieldName}
            </dt>
            <dd className="m-0 ml-1">
              <span className="text-[13px] text-[var(--color-text-secondary)]">
                <span className="text-[var(--color-status-danger)]">
                  <TruncatedValue value={change.oldValue} />
                </span>
                {' → '}
                <span className="text-[var(--color-status-success)]">
                  <TruncatedValue value={change.newValue} />
                </span>
              </span>
              {names.length > 0 && (
                <span className="ml-1 font-medium text-[var(--color-accent)]">· {names.join(', ')}</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
